import {
  DEFAULT_RETRY_CONFIG,
  computeRetryDelay,
  isRetryInProgress,
  shouldRetry
} from '@/lib/retry';
import { isTerminalReport, trackCommandInput } from '@/lib/terminalInput';
import { cn } from '@/lib/utils';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { WebglAddon } from '@xterm/addon-webgl';
import { Terminal } from '@xterm/xterm';
import { Terminal as TerminalIcon } from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import {
  sessionState,
  terminalState,
  terminalUrlState,
  useChatInteract
} from '@chainlit/react-client';
import type { ITerminal, TerminalComponentProps } from '@chainlit/react-client';

import { useTheme } from '@/components/ThemeProvider';
import {
  CardContent,
  CardHeader,
  CardSquare,
  CardTitle
} from '@/components/ui/card';

import { MOBILE_BREAKPOINT } from '@/hooks/use-mobile';

import {
  accountExpiredState,
  pendingTerminalHistoriesState,
  termsAcceptedSignalState
} from '@/state/project';

import '@xterm/xterm/css/xterm.css';

/**
 * Per-command record kept for the "Send terminal history to chat" toolbar
 * action. Mirrors the bounds used by the backend `TerminalState`
 * (`MAX_COMMAND_HISTORY` / `MAX_COMMAND_OUTPUT_CHARS` in
 * `src/crimson_raven/core/models/state/terminal_state.py`) so the frontend
 * and backend agree on how much history is meaningful to ship to the LLM.
 * Opened for operator-issued commands (`beginRecord`) AND AI-issued ones
 * (`recordAiCommand`), so a terminal an operator takes over from the AI
 * (#1126) already has real history to send instead of coming up empty.
 */
type UserTerminalCommandRecord = {
  command: string;
  output: string;
  timestamp: string;
};

const USER_TERMINAL_MAX_RECORDS = 50;
const USER_TERMINAL_MAX_OUTPUT_CHARS = 8000;

/**
 * xterm font size for the current viewport width — smaller on the mobile layout
 * so the terminal doesn't dwarf a phone-width pane. Re-applied on resize because
 * the terminal is one persistent instance shared across the mobile/desktop
 * layouts (matches the 1024px layout breakpoint in `useIsMobile`).
 */
const terminalFontSize = (width: number) =>
  width < MOBILE_BREAKPOINT ? 9 : 12;
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_PATTERN = /\[[0-9;]*[A-Za-z]/g;

/**
 * TerminalComponent
 *
 * A full-featured terminal emulator component for WebSocket-based SSH sessions.
 * Supports both interactive and read-only (AI) terminals, with real-time output streaming,
 * command history, and backend integration. Uses xterm.js for rendering and supports
 * advanced features like font ligatures, WebGL rendering, and responsive design.
 *
 * Props:
 * - wsUrl: WebSocket URL for the terminal backend
 * - showHeader: Show the terminal header (default: false)
 * - className: Additional CSS classes for the card
 * - onConnect: Callback when connected
 * - onDisconnect: Callback when disconnected
 * - onConnecting: Callback when connecting
 * - isReadOnly: If true, disables input and uses AI terminal logic
 * - onRegisterConnect: Register a connect function for parent control
 * - onReclaimControl: Called when an approved AI command targets this terminal
 *   while it is user-controlled (isReadOnly false) — the parent should flip
 *   isReadOnly back to true so the command can actually reach the PTY.
 * - autoConnect: If true, auto-connects on mount
 * - onAutoConnectHandled: Callback after auto-connect
 */
const TerminalComponent = forwardRef<
  any,
  TerminalComponentProps & {
    autoConnect?: boolean;
    onAutoConnectHandled?: () => void;
    initialTerminalId?: string;
  }
>(
  (
    {
      wsUrl = '',
      showHeader = false,
      className,
      onConnect,
      onDisconnect,
      onConnecting,
      isReadOnly = false,
      onRegisterConnect,
      onReclaimControl,
      autoConnect = true,
      onAutoConnectHandled,
      initialTerminalId
    },
    ref
  ) => {
    const { variant } = useTheme();
    const { sendTerminalData, sendAiTerminalData, sendTerminalConnection } =
      useChatInteract();
    const terminalUrl = useRecoilValue(terminalUrlState);
    const session = useRecoilValue(sessionState);
    const termsAcceptedSignal = useRecoilValue(termsAcceptedSignalState);
    const prevTermsSignalRef = useRef(termsAcceptedSignal);
    const terminalRef = useRef<HTMLDivElement>(null);
    const terminal = useRef<Terminal | null>(null);
    const terminalId = useRef<string>(
      initialTerminalId ||
        (isReadOnly
          ? 'ai-terminal'
          : `terminal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`)
    );
    const isReadOnlyRef = useRef(isReadOnly);
    useEffect(() => {
      isReadOnlyRef.current = isReadOnly;
    }, [isReadOnly]);

    const accountExpired = useRecoilValue(accountExpiredState);
    const accountExpiredRef = useRef(false);
    useEffect(() => {
      accountExpiredRef.current = !!accountExpired;
      if (terminal.current)
        terminal.current.options.disableStdin = !!accountExpired;
    }, [accountExpired]);

    useEffect(() => {
      console.log(
        `[Terminal] Initialized with ID: ${terminalId.current}, isReadOnly: ${isReadOnly}`
      );
    }, [isReadOnly]);

    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [sessionId, setSessionId] = useState<string>('');
    const [isSendingToBackend, setIsSendingToBackend] =
      useState<boolean>(false);
    const [portMappings, setPortMappings] = useState<Record<string, string>>(
      {}
    );
    const pendingAiCommandsRef = useRef<
      Map<
        string,
        { command: string; startTime: number; output: string; sent: boolean }
      >
    >(new Map());
    const pendingTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const [scrollback, setScrollback] = useState(10000);
    const [ranCommands, setRanCommands] = useState<Set<string>>(new Set());

    const retryCountRef = useRef<number>(0);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const maxRetriesReachedRef = useRef<boolean>(false);

    const terminals = useRecoilValue(terminalState) as unknown as Record<
      string,
      ITerminal
    >;
    const [pendingHistories, setPendingHistories] = useRecoilState(
      pendingTerminalHistoriesState
    );
    const connectWebSocketRef = useRef<(() => Promise<void>) | null>(null);
    const sendResizeRef = useRef<() => void>(() => {});

    const getExecutedUuids = useCallback((): Set<string> => {
      if (!isReadOnly) return new Set();
      try {
        const stored = localStorage.getItem('ai-terminal-executed-commands');
        if (stored) {
          const parsed = JSON.parse(stored);
          const oneDayAgo = Date.now() - 8 * 60 * 60 * 1000;
          const cleaned = Object.entries(parsed).filter(
            ([_, timestamp]: any) => timestamp > oneDayAgo
          );
          const cleanedObj = Object.fromEntries(cleaned);
          localStorage.setItem(
            'ai-terminal-executed-commands',
            JSON.stringify(cleanedObj)
          );
          return new Set(Object.keys(cleanedObj));
        }
      } catch (error) {
        console.warn(
          'Failed to load executed commands from localStorage:',
          error
        );
        localStorage.removeItem('ai-terminal-executed-commands');
      }
      return new Set();
    }, [isReadOnly]);

    const [executedUuids, setExecutedUuids] =
      useState<Set<string>>(getExecutedUuids);
    const executedUuidsRef = useRef<Set<string>>(getExecutedUuids());
    const lastAiCommandRef = useRef<{
      command: string;
      executionUuid: string;
    } | null>(null);
    const aiCommandRetryRef = useRef<{
      executionUuid: string;
      tried: boolean;
    } | null>(null);
    const connectionAttemptRef = useRef<boolean>(false);
    const prevSessionIdRef = useRef<string | undefined>();
    const pendingHistoryRef = useRef<string | null>(null);
    const commandRecordsRef = useRef<UserTerminalCommandRecord[]>([]);
    const activeRecordRef = useRef<UserTerminalCommandRecord | null>(null);
    const lineBufferRef = useRef<string>('');

    // Hold the volatile Chainlit session + connection flag in refs so the
    // socket-emitting callbacks and the mount-only xterm setup effect can read
    // the current values without listing `session`/`isConnected` as deps. A new
    // `sessionState` object is minted on every socket (re)connect
    // (useChatSession.ts), so depending on it would otherwise dispose and
    // rebuild the terminal on every reconnect.
    const sessionRef = useRef(session);
    useEffect(() => {
      sessionRef.current = session;
    }, [session]);

    const isConnectedRef = useRef(isConnected);
    useEffect(() => {
      isConnectedRef.current = isConnected;
    }, [isConnected]);

    /**
     * Close the in-flight command record (if any) into the bounded history
     * list and open a new active record for the supplied command. Pure ref
     * bookkeeping shared by `beginRecord` (operator-issued) and
     * `recordAiCommand` (AI-issued) so either source can populate the
     * history `sendHistoryToChat` sends.
     */
    const openCommandRecord = useCallback((command: string) => {
      if (activeRecordRef.current) {
        commandRecordsRef.current.push(activeRecordRef.current);
        if (commandRecordsRef.current.length > USER_TERMINAL_MAX_RECORDS) {
          commandRecordsRef.current.splice(
            0,
            commandRecordsRef.current.length - USER_TERMINAL_MAX_RECORDS
          );
        }
      }
      activeRecordRef.current = {
        command,
        output: '',
        timestamp: new Date().toISOString()
      };
    }, []);

    /**
     * Opens a command record for an operator-issued command and emits the
     * `terminal_command` audit event. The backend tags that event
     * `terminal_kind="user"` unconditionally, so this must stay
     * operator-only — read-only call sites already guard before calling
     * this; the check here is a defensive backstop.
     */
    const beginRecord = useCallback(
      (command: string) => {
        if (isReadOnlyRef.current) return;
        openCommandRecord(command);
        if (sessionRef.current?.socket && isConnectedRef.current) {
          sessionRef.current.socket.emit('terminal_command', {
            terminal_id: terminalId.current,
            command
          });
        }
      },
      [openCommandRecord]
    );

    /**
     * Opens a command record for an AI-issued command — the record-keeping
     * half of `beginRecord`, without the operator-audit emit. AI commands
     * are already audited server-side via the executor's shell tool
     * (`terminal_kind="ai"`), so this only exists to make sure a terminal an
     * operator later takes over from the AI has real history ready for
     * "Send terminal history to chat" instead of an empty buffer.
     */
    const recordAiCommand = useCallback(
      (command: string) => {
        openCommandRecord(command);
      },
      [openCommandRecord]
    );

    const clearRetryTimeout = useCallback(() => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }, []);

    const resetConnectionState = useCallback(() => {
      connectionAttemptRef.current = false;
      retryCountRef.current = 0;
      maxRetriesReachedRef.current = false;
      setIsConnected(false);
      setIsConnecting(false);
      setPortMappings({});
      pendingAiCommandsRef.current.clear();
      pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current.clear();
      aiCommandRetryRef.current = null;
      lastAiCommandRef.current = null;
      clearRetryTimeout();
    }, [clearRetryTimeout]);

    useEffect(() => {
      const myHistory = pendingHistories[terminalId.current];
      if (!myHistory) return;
      if (terminal.current && isConnected) {
        terminal.current.write(myHistory);
        console.log(`[Terminal] Restored history for ${terminalId.current}`);
        setPendingHistories((prev) => {
          if (!(terminalId.current in prev)) return prev;
          const next = { ...prev };
          delete next[terminalId.current];
          return next;
        });
      } else {
        pendingHistoryRef.current = myHistory;
        console.log(
          `[Terminal] Buffered history for ${terminalId.current} (waiting for connection)`
        );
      }
    }, [pendingHistories, isConnected, setPendingHistories]);

    useEffect(() => {
      const currentSessionId = session?.socket?.id;

      if (
        prevSessionIdRef.current &&
        currentSessionId &&
        prevSessionIdRef.current !== currentSessionId
      ) {
        console.log(
          '[Terminal] Session ID changed, cleaning up stale connection state'
        );
        resetConnectionState();
      }

      prevSessionIdRef.current = currentSessionId;
    }, [session?.socket?.id, resetConnectionState]);

    useEffect(() => {
      if (!session?.socket) return;

      const handleSocketDisconnect = () => {
        console.log('[Terminal] Socket.IO disconnected');
        // Mark as disconnected but preserve retry counters so Socket.IO
        // reconnection does not restart the retry storm.
        clearRetryTimeout();
        connectionAttemptRef.current = false;
        setIsConnected(false);
        setIsConnecting(false);
        onDisconnect?.();
      };

      session.socket.on('disconnect', handleSocketDisconnect);

      return () => {
        session.socket.off('disconnect', handleSocketDisconnect);
      };
    }, [session?.socket, clearRetryTimeout, onDisconnect]);

    const markCommandExecuted = useCallback(
      (executionUuid: string) => {
        if (!isReadOnly || !executionUuid) return;

        executedUuidsRef.current.add(executionUuid);
        setExecutedUuids(new Set(executedUuidsRef.current));
        try {
          const stored =
            localStorage.getItem('ai-terminal-executed-commands') || '{}';
          const parsed = JSON.parse(stored);
          parsed[executionUuid] = Date.now();
          localStorage.setItem(
            'ai-terminal-executed-commands',
            JSON.stringify(parsed)
          );
        } catch (error) {
          console.warn(
            'Failed to save executed command to localStorage:',
            error
          );
        }
      },
      [isReadOnly]
    );

    const generateUUID = (): string => {
      if (typeof crypto !== 'undefined' && crypto.randomUUID)
        return crypto.randomUUID();
      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        return Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
      }
      return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };

    useEffect(() => {
      let commandToSend: { command: string; executionUuid: string } | null =
        null;

      Object.entries(terminals).forEach(([identifier, terminalData]) => {
        if (terminalData.identifier !== 'AI_EXECUTE') return;
        const executionUuid = (terminalData as any).execution_uuid;
        if (executionUuid && executedUuidsRef.current.has(executionUuid))
          return;

        const targetId = (terminalData as any).target_terminal_id;
        if (!targetId) return;
        if (targetId !== terminalId.current) return;

        commandToSend = { command: terminalData.command, executionUuid };
      });

      // An approved command targeting this terminal while the operator has
      // taken over (isReadOnly false) would otherwise sit in `terminals`
      // forever, since nothing below runs until isReadOnly flips back. Ask
      // the parent to reclaim control; once isReadOnly becomes true this
      // effect reruns and falls through to the send logic below.
      if (commandToSend && !isReadOnly) {
        onReclaimControl?.();
        return;
      }
      if (!isReadOnly) return;

      const trySendCommand = () => {
        if (
          session?.socket &&
          isConnected &&
          commandToSend &&
          typeof commandToSend.executionUuid === 'string' &&
          !executedUuidsRef.current.has(commandToSend.executionUuid)
        ) {
          const { executionUuid, command } = commandToSend;
          markCommandExecuted(executionUuid);
          recordAiCommand(command);
          session.socket.emit('terminal_io', {
            terminal_id: terminalId.current,
            type: 'command',
            data: command
          });
          pendingAiCommandsRef.current.set(executionUuid, {
            command,
            startTime: Date.now(),
            output: '',
            sent: false
          });
          lastAiCommandRef.current = { command, executionUuid };
          aiCommandRetryRef.current = null;
          commandToSend = null;
          return;
        }
        if (
          commandToSend &&
          typeof commandToSend.executionUuid === 'string' &&
          !executedUuidsRef.current.has(commandToSend.executionUuid)
        ) {
          if (
            !aiCommandRetryRef.current ||
            aiCommandRetryRef.current.executionUuid !==
              commandToSend.executionUuid
          ) {
            aiCommandRetryRef.current = {
              executionUuid: commandToSend.executionUuid,
              tried: false
            };
          }
          if (!aiCommandRetryRef.current.tried) {
            aiCommandRetryRef.current.tried = true;
            setTimeout(() => {
              if (
                session?.socket &&
                isConnected &&
                commandToSend &&
                typeof commandToSend.executionUuid === 'string' &&
                !executedUuidsRef.current.has(commandToSend.executionUuid)
              ) {
                const { executionUuid, command } = commandToSend;
                markCommandExecuted(executionUuid);
                recordAiCommand(command);
                session.socket.emit('terminal_io', {
                  terminal_id: terminalId.current,
                  type: 'command',
                  data: command
                });
                pendingAiCommandsRef.current.set(executionUuid, {
                  command,
                  startTime: Date.now(),
                  output: '',
                  sent: false
                });
                lastAiCommandRef.current = { command, executionUuid };
                aiCommandRetryRef.current = null;
                commandToSend = null;
              }
            }, 2000);
          }
        }
      };

      trySendCommand();
    }, [
      terminals,
      isReadOnly,
      markCommandExecuted,
      recordAiCommand,
      session,
      isConnected,
      onReclaimControl
    ]);

    /**
     * Post the terminal's recorded command/output pairs into the chat
     * thread as a markdown-formatted user message so the LLM can reason over
     * recent work. ``scope`` accepts ``'last'`` (most recent one command),
     * ``'all'`` (full bounded history), or a positive integer N (last N
     * commands, clamped into ``[1, totalAvailable]``). No-op while the
     * terminal is currently read-only (AI-controlled) — the toolbar doesn't
     * surface this control until the operator takes over, at which point
     * `commandRecordsRef` already holds the AI's command history (tracked by
     * `recordAiCommand` while read-only) alongside anything persisted in the
     * backend's own ``TerminalState``. Returns the number of records
     * actually sent so callers can short-circuit empty sends.
     */
    const sendHistoryToChat = useCallback(
      (scope: 'last' | 'all' | number): number => {
        if (isReadOnlyRef.current) return 0;

        const all = [...commandRecordsRef.current];
        if (activeRecordRef.current) all.push(activeRecordRef.current);
        if (all.length === 0) return 0;

        let count: number;
        if (scope === 'all') {
          count = all.length;
        } else if (scope === 'last') {
          count = 1;
        } else {
          if (!Number.isFinite(scope) || scope <= 0) return 0;
          count = Math.min(Math.floor(scope), all.length);
        }
        const slice = all.slice(-count);
        if (slice.length === 0) return 0;

        const cleanOutput = (raw: string): string => {
          const tail =
            raw.length > USER_TERMINAL_MAX_OUTPUT_CHARS
              ? raw.slice(-USER_TERMINAL_MAX_OUTPUT_CHARS)
              : raw;
          return tail
            .replace(ANSI_ESCAPE_PATTERN, '')
            .replace(/\r/g, '')
            .trimEnd();
        };

        const body = slice
          .map(
            (r) =>
              `**$ ${r.command}**\n\n\`\`\`console\n${cleanOutput(r.output)}\n\`\`\``
          )
          .join('\n\n');
        const header =
          scope === 'all'
            ? `### Terminal history — all ${slice.length} command(s)`
            : `### Terminal history — last ${slice.length} command(s)`;

        setIsSendingToBackend(true);
        sendTerminalData({
          timestamp: new Date().toISOString(),
          command: 'terminal_history_export',
          output: `${header}\n\n${body}`,
          status: 'success',
          sessionId: sessionId,
          type: 'terminal_history_export',
          execution_uuid: generateUUID()
        });
        setIsSendingToBackend(false);
        return slice.length;
      },
      [sendTerminalData, sessionId]
    );

    const terminalThemes = {
      dark: {
        background: '#0a0a0a',
        foreground: '#e5e5e5',
        cursor: '#00ff00',
        selection: 'rgba(0, 255, 0, 0.3)',
        black: '#000000',
        red: '#ff5555',
        green: '#50fa7b',
        yellow: '#f1fa8c',
        blue: '#bd93f9',
        magenta: '#ff79c6',
        cyan: '#8be9fd',
        white: '#f8f8f2',
        brightBlack: '#6272a4',
        brightRed: '#ff6e6e',
        brightGreen: '#69ff94',
        brightYellow: '#ffffa5',
        brightBlue: '#d6acff',
        brightMagenta: '#ff92df',
        brightCyan: '#a4ffff',
        brightWhite: '#ffffff'
      },
      light: {
        background: '#ffffff',
        foreground: '#2d3748',
        cursor: '#2d3748',
        selection: 'rgba(45, 55, 72, 0.3)',
        black: '#000000',
        red: '#e53e3e',
        green: '#38a169',
        yellow: '#d69e2e',
        blue: '#3182ce',
        magenta: '#d53f8c',
        cyan: '#319795',
        white: '#f7fafc',
        brightBlack: '#718096',
        brightRed: '#fc8181',
        brightGreen: '#68d391',
        brightYellow: '#f6e05e',
        brightBlue: '#63b3ed',
        brightMagenta: '#f687b3',
        brightCyan: '#4fd1c7',
        brightWhite: '#ffffff'
      }
    };

    const tokenStyles = {
      card: 'bg-card border-border',
      header: 'bg-card/85 border-border',
      content: 'bg-card border-border',
      icon: 'bg-primary/15 border-primary/30',
      iconColor: 'text-primary',
      title: 'text-foreground',
      subtitle: 'text-muted-foreground',
      badge: 'bg-muted/60 text-muted-foreground border-border',
      badgeActive: 'bg-primary/15 text-primary border-primary/30',
      statusConnecting: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
      statusConnected: 'bg-primary/15 text-primary border-primary/30',
      statusDisconnected:
        'bg-destructive/15 text-destructive border-destructive/30',
      button:
        'bg-muted/60 border-border text-foreground hover:bg-accent hover:text-foreground',
      buttonConnect:
        'bg-primary/15 border-primary/30 text-primary hover:bg-primary/25 hover:text-primary',
      buttonDisconnect:
        'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
    };
    const cardStyles = {
      dark: tokenStyles,
      light: tokenStyles
    };
    const currentStyles = cardStyles[variant];

    const connectWebSocketInternal = useCallback(async () => {
      if (!session?.socket) {
        console.log('[Terminal] No socket session available');
        return;
      }

      console.log(
        `[Terminal] Attempting connection (try ${retryCountRef.current + 1}/${DEFAULT_RETRY_CONFIG.maxRetries + 1})...`
      );
      connectionAttemptRef.current = true;
      setIsConnecting(true);
      onConnecting?.();

      session.socket.emit('terminal_connect', {
        terminal_id: terminalId.current,
        user_id: (session as any).user?.identifier
      });
    }, [session, onConnecting]);

    const scheduleRetry = useCallback(() => {
      if (!shouldRetry(retryCountRef.current, maxRetriesReachedRef.current)) {
        console.log(
          '[Terminal] Max retries reached, stopping automatic reconnection'
        );
        maxRetriesReachedRef.current = true;
        setIsConnecting(false);
        connectionAttemptRef.current = false;
        terminal.current?.writeln(
          '\r\n\x1B[1;33mConnection lost. Click "Connect" to try again.\x1B[0m'
        );
        return;
      }

      clearRetryTimeout();
      retryCountRef.current += 1;
      const delay = computeRetryDelay(retryCountRef.current);

      console.log(
        `[Terminal] Scheduling retry ${retryCountRef.current}/${DEFAULT_RETRY_CONFIG.maxRetries} in ${delay}ms`
      );

      terminal.current?.writeln(
        `\r\n\x1B[1;33mRetrying in ${(delay / 1000).toFixed(1)}s (attempt ${retryCountRef.current}/${DEFAULT_RETRY_CONFIG.maxRetries})...\x1B[0m`
      );

      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        connectWebSocketInternal();
      }, delay);
    }, [clearRetryTimeout, connectWebSocketInternal]);

    const connectWebSocket = useCallback(async () => {
      if (connectionAttemptRef.current || isConnected || isConnecting) {
        console.log(
          '[Terminal] Connection already in progress or connected, skipping'
        );
        return;
      }

      clearRetryTimeout();
      retryCountRef.current = 0;
      maxRetriesReachedRef.current = false;

      console.log('[Terminal] Manual connection attempt initiated');
      connectWebSocketInternal();
    }, [
      isConnected,
      isConnecting,
      clearRetryTimeout,
      connectWebSocketInternal
    ]);

    const forceReconnect = useCallback(async () => {
      console.log('[Terminal] Force reconnecting - resetting all state');
      resetConnectionState();

      await new Promise((resolve) => setTimeout(resolve, 50));

      if (session?.socket) {
        connectWebSocketInternal();
      }
    }, [resetConnectionState, session?.socket, connectWebSocketInternal]);

    useEffect(() => {
      connectWebSocketRef.current = connectWebSocket;
    }, [connectWebSocket]);

    useEffect(() => {
      if (!session?.socket) return;

      const handleSocketConnect = () => {
        console.log('[Terminal] Socket.IO reconnected');
        // Only clear in-flight flags. The auto-connect useEffect will
        // evaluate whether to attempt a new connection — if retries were
        // exhausted it will reset the counter via connectWebSocket().
        connectionAttemptRef.current = false;
        setIsConnecting(false);
      };

      session.socket.on('connect', handleSocketConnect);

      return () => {
        session.socket.off('connect', handleSocketConnect);
      };
    }, [session?.socket]);

    useEffect(() => {
      if (typeof onRegisterConnect === 'function') {
        const stableConnectWrapper = async () => {
          if (connectWebSocketRef.current) {
            await connectWebSocketRef.current();
          }
        };
        console.log(
          '[Terminal] Registering stable connect function with parent'
        );
        onRegisterConnect(stableConnectWrapper);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRegisterConnect]);

    useEffect(() => {
      // On each failed attempt the error handler flips isConnecting true
      // -> false; without this guard the effect would re-enter and reset
      // retryCountRef to 0 via connectWebSocket() — the #869 storm.
      if (
        autoConnect &&
        !isConnected &&
        !isConnecting &&
        session?.socket &&
        !maxRetriesReachedRef.current &&
        !isRetryInProgress(retryCountRef.current, retryTimeoutRef.current)
      ) {
        console.log('[Terminal] Auto-connecting...');
        connectWebSocket();
        if (onAutoConnectHandled) onAutoConnectHandled();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoConnect, isConnected, isConnecting, session]);

    useEffect(() => {
      // Operator just accepted the legal/privacy gate. The mount-time connect
      // was refused ('disabled' → maxRetriesReached), so re-issue the same
      // attempt the Connect button makes. connectWebSocket no-ops when already
      // connected/connecting, so this is safe for terminals that were never gated.
      if (termsAcceptedSignal !== prevTermsSignalRef.current) {
        prevTermsSignalRef.current = termsAcceptedSignal;
        connectWebSocket();
      }
    }, [termsAcceptedSignal, connectWebSocket]);

    const sendInput = useCallback((data: string) => {
      if (accountExpiredRef.current) return;
      if (sessionRef.current?.socket && isConnectedRef.current) {
        sessionRef.current.socket.emit('terminal_io', {
          terminal_id: terminalId.current,
          type: 'input',
          data: data
        });
      }
    }, []);

    sendResizeRef.current = () => {
      if (terminal.current && session?.socket && isConnected) {
        session.socket.emit('terminal_io', {
          terminal_id: terminalId.current,
          type: 'resize',
          data: { cols: terminal.current.cols, rows: terminal.current.rows }
        });
      }
    };

    const sendCommand = useCallback((command: string) => {
      if (accountExpiredRef.current) return;
      if (sessionRef.current?.socket && isConnectedRef.current) {
        sessionRef.current.socket.emit('terminal_io', {
          terminal_id: terminalId.current,
          type: 'command',
          data: command
        });
      } else {
        terminal.current?.writeln('\x1B[1;31mNot connected to server\x1B[0m');
      }
    }, []);

    /**
     * Sends a disconnect message to the backend
     */
    const sendDisconnect = useCallback(() => {
      if (session?.socket && isConnected) {
        session.socket.emit('terminal_disconnect', {
          terminal_id: terminalId.current
        });
      }
    }, [session, isConnected]);

    /**
     * Disconnects from the terminal via backend
     */
    const disconnectWebSocket = useCallback(() => {
      console.log('[Terminal] Disconnecting...');

      clearRetryTimeout();
      retryCountRef.current = 0;
      maxRetriesReachedRef.current = false;

      sendDisconnect();

      setIsConnected(false);
      setIsConnecting(false);
      setSessionId('');
      onDisconnect?.();
    }, [sendDisconnect, onDisconnect, clearRetryTimeout]);

    /**
     * Requests session information from the backend
     */
    const getSessions = useCallback(() => {
      if (session?.socket && isConnected) {
        session.socket.emit('terminal_io', {
          terminal_id: terminalId.current,
          type: 'get_sessions'
        });
      }
    }, [session, isConnected]);

    /**
     * Clears the terminal
     */
    const clearTerminal = useCallback(() => {
      if (terminal.current) {
        terminal.current.clear();
        commandRecordsRef.current = [];
        activeRecordRef.current = null;
        lineBufferRef.current = '';
        setTimeout(() => terminal.current?.scrollToBottom(), 0);
      }
    }, []);

    /** Resets the executed command tracking to allow re-execution. */
    const clearExecutedCommands = useCallback(() => {
      if (!isReadOnly) return;

      executedUuidsRef.current.clear();
      setExecutedUuids(new Set());

      try {
        localStorage.removeItem('ai-terminal-executed-commands');
        console.log('[AI Terminal] Cleared executed commands history');
      } catch (error) {
        console.warn(
          'Failed to clear executed commands from localStorage:',
          error
        );
      }
    }, [isReadOnly]);

    /**
     * Handles command submission from the input box.
     */
    const handleCommandSubmit = useCallback(
      (command: string) => {
        const trimmed = command.trim();
        if (!trimmed) {
          sendCommand('');
          return;
        }

        setRanCommands((prev) => {
          if (prev.has(trimmed)) {
            terminal.current?.writeln(
              `\x1B[1;33m[Warning] Command already run: ${trimmed}\x1B[0m`
            );
            return prev;
          }

          beginRecord(trimmed);
          lineBufferRef.current = '';
          sendCommand(trimmed);
          return new Set([...prev, trimmed]);
        });
      },
      [sendCommand, beginRecord]
    );

    /**
     * Initializes the terminal instance with xterm.js and configures:
     * - Core terminal settings (fonts, colors, dimensions)
     * - Essential addons (fit, weblinks, search, WebGL)
     * - Input handling for interactive terminals
     * - Copy/paste functionality
     * - Unicode and ligature support
     *
     * Only runs once on mount when terminal ref is available.
     */
    useEffect(() => {
      if (!terminalRef.current) return;
      if (terminal.current) return;

      terminal.current = new Terminal({
        allowProposedApi: true,
        cursorBlink: true,
        fontSize: terminalFontSize(window.innerWidth),
        fontFamily: 'JetBrains Mono, Menlo, Monaco, "Courier New", monospace',
        theme: terminalThemes[variant],
        convertEol: false,
        disableStdin: accountExpiredRef.current,
        scrollback: 10000,
        cursorStyle: 'block',
        allowTransparency: true,
        scrollOnUserInput: false,
        cols: 80,
        rows: 24,
        lineHeight: 1.15,
        letterSpacing: 0,
        smoothScrollDuration: 0,
        fastScrollModifier: 'alt',
        fastScrollSensitivity: 5
      });

      const fitAddon = new FitAddon();
      const webLinksAddon = new WebLinksAddon();
      const searchAddon = new SearchAddon();

      terminal.current.loadAddon(fitAddon);
      terminal.current.loadAddon(webLinksAddon);
      terminal.current.loadAddon(searchAddon);

      terminal.current.open(terminalRef.current);
      fitAddon.fit();

      if (isReadOnlyRef.current && sessionRef.current?.socket) {
        sessionRef.current.socket.emit('spawn_ai_terminal_ack', {
          terminal_id: terminalId.current
        });
      }

      // The WebGL renderer paints blank on many mobile GPUs. The terminal is a
      // single persistent instance created once, so this choice can't be redone
      // on a later resize — gate on a coarse (touch) primary pointer, not just
      // width, so a landscape-first tablet rotated to portrait never ends up on
      // WebGL. Fine-pointer desktops keep it; touch devices use the DOM renderer.
      // Tracked so the desktop->mobile resize path (handleResize) can dispose it.
      // The renderer choice is made once at mount from the initial viewport, so a
      // terminal created on a fine-pointer desktop keeps WebGL when the viewport
      // later crosses to mobile width — where it paints blank on many mobile GPUs.
      let webglAddon: WebglAddon | null = null;
      const prefersDomRenderer =
        window.innerWidth < MOBILE_BREAKPOINT ||
        window.matchMedia('(pointer: coarse)').matches;
      if (!prefersDomRenderer) {
        try {
          webglAddon = new WebglAddon();
          webglAddon.onContextLoss(() => {
            webglAddon?.dispose();
            webglAddon = null;
          });
          terminal.current.loadAddon(webglAddon);
        } catch {
          console.warn('[Terminal] WebGL addon failed, using default renderer');
        }
      }

      // The persistent terminal is created once and, on mobile, mounts into a
      // pane that is laid out (for dimensions) but not the active view, so the
      // fit above can measure a container that hasn't reached its final size on
      // some devices. Re-fit on the next frame (the ResizeObserver + `resize`
      // handler cover later changes) so it lands at the right cols/rows without
      // needing a manual resize a static mobile open never produces.
      requestAnimationFrame(() => {
        if (
          terminal.current &&
          terminalRef.current &&
          terminalRef.current.clientWidth > 0 &&
          terminalRef.current.clientHeight > 0
        ) {
          fitAddon.fit();
          sendResizeRef.current?.();
        }
      });

      // Copy/paste shortcuts are intercepted before xterm turns them into data:
      // returning false from the custom key handler stops the keystroke, so it is
      // NOT also relayed to the PTY by the onData handler below. Everything else
      // falls through and reaches onData as normal terminal data.
      terminal.current.attachCustomKeyEventHandler((ev) => {
        if (ev.type !== 'keydown') return true;
        const isModifierKey = ev.ctrlKey || ev.metaKey;

        if (isModifierKey && (ev.key === 'c' || ev.key === 'C')) {
          if (terminal.current?.hasSelection()) {
            const selectedText = terminal.current.getSelection();
            navigator.clipboard.writeText(selectedText).catch((err) => {
              console.error('[Terminal] Failed to copy text:', err);
              terminal.current?.writeln(
                '\r\n\x1B[1;33mFailed to copy to clipboard\x1B[0m'
              );
            });
            return false;
          }
          if (!isReadOnlyRef.current) {
            sendInput('\x03');
          }
          return false;
        }

        if (
          !isReadOnlyRef.current &&
          isModifierKey &&
          (ev.key === 'v' || ev.key === 'V')
        ) {
          navigator.clipboard
            .readText()
            .then((text) => {
              if (text) {
                sendInput(text);
                setTimeout(() => terminal.current?.scrollToBottom(), 0);
              }
            })
            .catch((err) => {
              console.error('[Terminal] Failed to read from clipboard:', err);
              terminal.current?.writeln(
                '\r\n\x1B[1;33mFailed to read from clipboard\x1B[0m'
              );
            });
          return false;
        }

        return true;
      });

      // Relay terminal data to the PTY via onData, not just keystrokes. onData
      // also carries the terminal's own replies to host queries — DSR
      // cursor-position reports, device attributes. Interactive tools (e.g.
      // evil-winrm's Reline) probe the terminal on startup and block waiting for
      // that reply; the old onKey binding never saw programmatic responses, so
      // the session hung right after printing the probe glyph.
      //
      // Read-only AI terminals share one PTY with the AI executor (both keyed by
      // this terminal_id), so a tool the AI launches probes THIS xterm. Those
      // terminals must answer capability queries but must never forward operator
      // keystrokes or paste — so they relay device-report replies only. They keep
      // stdin enabled (disableStdin is expiry-only): xterm gates report replies
      // behind the same disableStdin check as user input, so muting stdin would
      // mute the reply too.
      terminal.current.onData((data) => {
        if (accountExpiredRef.current) return;
        if (isReadOnlyRef.current) {
          if (isTerminalReport(data)) sendInput(data);
          return;
        }
        const { buffer, commands } = trackCommandInput(
          lineBufferRef.current,
          data
        );
        lineBufferRef.current = buffer;
        commands.forEach((command) => beginRecord(command));
        sendInput(data);
        setTimeout(() => terminal.current?.scrollToBottom(), 0);
      });

      const unicodeAddon = new Unicode11Addon();
      terminal.current.loadAddon(unicodeAddon);
      terminal.current.unicode.activeVersion = '11';

      terminalRef.current.setAttribute('aria-label', 'Terminal window');
      terminalRef.current.setAttribute('tabIndex', '0');

      if (!isReadOnlyRef.current) {
        setTimeout(() => {
          terminal.current?.focus();
        }, 0);
      }

      if (terminalRef.current) {
        terminalRef.current.style.touchAction = 'pan-y';
      }

      const viewport =
        terminal.current.element?.querySelector('.xterm-viewport');
      let scrollHandler: (() => void) | null = null;
      let scrollTimeout: NodeJS.Timeout | null = null;

      if (viewport) {
        scrollHandler = () => {
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          scrollTimeout = setTimeout(() => {
            const isAtBottom =
              viewport.scrollTop + viewport.clientHeight >=
              viewport.scrollHeight - 5;
          }, 100);
        };

        viewport.addEventListener('scroll', scrollHandler);
      }

      let resizeTimeout: NodeJS.Timeout | null = null;
      const handleResize = () => {
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (terminal.current && fitAddon && terminalRef.current) {
            if (
              terminalRef.current.clientWidth === 0 ||
              terminalRef.current.clientHeight === 0
            )
              return;
            const nextFontSize = terminalFontSize(window.innerWidth);
            if (terminal.current.options.fontSize !== nextFontSize) {
              terminal.current.options.fontSize = nextFontSize;
            }
            // Crossing into mobile width: drop the WebGL renderer (blank on many
            // mobile GPUs). Disposing reverts the terminal to xterm's DOM renderer
            // in place; refit on the next frame so the DOM renderer's char metrics
            // are measured before sizing, otherwise cols stay wide and overflow.
            if (window.innerWidth < MOBILE_BREAKPOINT && webglAddon) {
              try {
                webglAddon.dispose();
              } catch {
                /* already disposed via context-loss */
              }
              webglAddon = null;
              requestAnimationFrame(() => {
                if (
                  terminal.current &&
                  terminalRef.current &&
                  terminalRef.current.clientWidth > 0 &&
                  terminalRef.current.clientHeight > 0
                ) {
                  fitAddon.fit();
                  sendResizeRef.current?.();
                }
              });
            }
            fitAddon.fit();
            sendResizeRef.current();
          }
        }, 100);
      };
      const resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      if (terminalRef.current) {
        resizeObserver.observe(terminalRef.current);
      }
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        if (resizeTimeout) clearTimeout(resizeTimeout);

        if (scrollHandler && viewport) {
          viewport.removeEventListener('scroll', scrollHandler);
        }
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }

        terminal.current?.dispose();
        terminal.current = null;

        connectionAttemptRef.current = false;

        pendingTimeoutsRef.current.forEach((timeout) => {
          clearTimeout(timeout);
        });
        pendingTimeoutsRef.current.clear();
        pendingAiCommandsRef.current.clear();
        aiCommandRetryRef.current = null;
        lastAiCommandRef.current = null;

        clearRetryTimeout();
        retryCountRef.current = 0;
        maxRetriesReachedRef.current = false;

        if (sessionRef.current?.socket && isConnectedRef.current) {
          sessionRef.current.socket.emit('terminal_disconnect', {
            terminal_id: terminalId.current
          });
        }
      };
      // The terminal instance is created once on mount and disposed on unmount.
      // It must NOT be torn down when the Chainlit `session` reference or the
      // connection flag changes (every socket reconnect mints a new session
      // object), so deps stay empty. The connection lifecycle (reconnect,
      // listeners, auto-connect) is handled by the socket-id and status effects
      // without destroying the xterm buffer.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (terminal.current && terminal.current.options) {
        terminal.current.options.theme = terminalThemes[variant];
        terminal.current.write('');
      }
    }, [variant]);

    useEffect(() => {
      if (terminal.current && !isReadOnly) {
        terminal.current.focus();
      }
    }, [isReadOnly]);

    useEffect(() => {
      if (!session?.socket) return;

      const socket = session.socket;
      const currentTerminalId = terminalId.current;

      const handleTerminalOutput = (data: any) => {
        if (data.terminal_id !== currentTerminalId) return;

        console.log('[Terminal] Received output:', data);
        if (data.data) {
          terminal.current?.write(data.data);
          setTimeout(() => terminal.current?.scrollToBottom(), 0);

          if (activeRecordRef.current) {
            const merged = activeRecordRef.current.output + data.data;
            activeRecordRef.current.output =
              merged.length > USER_TERMINAL_MAX_OUTPUT_CHARS
                ? merged.slice(-USER_TERMINAL_MAX_OUTPUT_CHARS)
                : merged;
          }

          if (isReadOnly) {
            const pendingEntries = Array.from(
              pendingAiCommandsRef.current.entries()
            );
            if (pendingEntries.length > 0) {
              const [executionUuid, commandInfo] =
                pendingEntries[pendingEntries.length - 1];
              pendingAiCommandsRef.current.set(executionUuid, {
                ...commandInfo,
                output: commandInfo.output + data.data
              });
              const payload = {
                timestamp: new Date().toISOString(),
                command: commandInfo.command,
                output: data.data,
                status: 'stream',
                sessionId: sessionId,
                type: 'ai_command',
                execution_uuid: executionUuid
              };
              sendAiTerminalData(payload);
            }
          }
        }
      };

      const handleTerminalError = (data: any) => {
        if (data.terminal_id !== currentTerminalId) return;

        console.log('[Terminal] Received error:', data);
        terminal.current?.writeln(`\r\n\x1B[1;31mError: ${data.error}\x1B[0m`);
        setTimeout(() => terminal.current?.scrollToBottom(), 0);
      };

      const handleTerminalStatus = (data: any) => {
        if (data.terminal_id !== currentTerminalId) return;

        console.log('[Terminal] Status update:', data);

        switch (data.status) {
          case 'connecting':
            setIsConnecting(true);
            setIsConnected(false);
            terminal.current?.writeln(
              `\x1B[1;33m${data.message || 'Connecting...'}\x1B[0m`
            );
            break;
          case 'connected':
          case 'ready':
            clearRetryTimeout();
            retryCountRef.current = 0;
            maxRetriesReachedRef.current = false;

            setIsConnecting(false);
            setIsConnected(true);
            connectionAttemptRef.current = false;
            onConnect?.();
            terminal.current?.writeln(
              `\x1B[1;32m${data.message || 'Connected'}\x1B[0m`
            );
            aiCommandRetryRef.current = null;
            setTimeout(() => sendResizeRef.current(), 200);
            if (pendingHistoryRef.current) {
              const historyToWrite = pendingHistoryRef.current;
              pendingHistoryRef.current = null;
              // Delay so the SSH login banner and prompt render first
              setTimeout(() => {
                if (terminal.current) {
                  terminal.current.write(historyToWrite);
                  console.log(
                    `[Terminal] Flushed buffered history for ${terminalId.current}`
                  );
                }
              }, 500);
            }
            break;
          case 'disconnected':
            setIsConnected(false);
            setIsConnecting(false);
            connectionAttemptRef.current = false;
            onDisconnect?.();
            terminal.current?.writeln(
              `\x1B[1;31m${data.message || 'Disconnected'}\x1B[0m`
            );
            if (!maxRetriesReachedRef.current) {
              scheduleRetry();
            } else {
              terminal.current?.writeln(
                '\x1B[1;33mClick "Connect" to reconnect.\x1B[0m'
              );
            }
            break;
          case 'disabled':
            clearRetryTimeout();
            retryCountRef.current = 0;
            maxRetriesReachedRef.current = true;

            setIsConnected(false);
            setIsConnecting(false);
            connectionAttemptRef.current = false;
            onDisconnect?.();
            terminal.current?.writeln(
              `\x1B[1;33m${data.message || 'Terminal disabled'}\x1B[0m`
            );
            break;
          case 'error':
            setIsConnected(false);
            setIsConnecting(false);
            connectionAttemptRef.current = false;
            onDisconnect?.();

            terminal.current?.writeln(
              `\x1B[1;31mConnection failed: ${data.error || data.message}\x1B[0m`
            );
            if (
              shouldRetry(retryCountRef.current, maxRetriesReachedRef.current)
            ) {
              scheduleRetry();
            } else {
              maxRetriesReachedRef.current = true;
              terminal.current?.writeln(
                '\x1B[1;33mConnection lost. Click "Connect" to try again.\x1B[0m'
              );
            }
            break;
          default:
            if (data.message) {
              terminal.current?.writeln(data.message);
            }
        }
        setTimeout(() => terminal.current?.scrollToBottom(), 0);
      };

      const handleSessionsInfo = (data: any) => {
        if (data.terminal_id !== currentTerminalId) return;

        console.log('[Terminal] Sessions info:', data);
        if (data.data) {
          terminal.current?.writeln(`\r\n\x1B[1;36mSessions Info:\x1B[0m`);
          terminal.current?.writeln(
            `Total sessions: ${data.data.total_sessions}`
          );
          terminal.current?.writeln(
            `Active sessions: ${data.data.active_sessions?.join(', ') || 'None'}`
          );
          terminal.current?.writeln(
            `Current session: ${data.data.current_session || 'N/A'}`
          );
          setTimeout(() => terminal.current?.scrollToBottom(), 0);
        }
      };

      const handlePortMappings = (data: any) => {
        if (data.terminal_id !== currentTerminalId) return;

        console.log('[Terminal] Port mappings received:', data);
        if (data.type === 'port_mappings' && data.data) {
          setPortMappings(data.data);
          setTimeout(() => terminal.current?.scrollToBottom(), 0);
        }
      };

      socket.on('terminal_output', handleTerminalOutput);
      socket.on('terminal_error', handleTerminalError);
      socket.on('terminal_status', handleTerminalStatus);
      socket.on('terminal_sessions_info', handleSessionsInfo);
      socket.on('terminal_port_mappings', handlePortMappings);

      return () => {
        socket.off('terminal_output', handleTerminalOutput);
        socket.off('terminal_error', handleTerminalError);
        socket.off('terminal_status', handleTerminalStatus);
        socket.off('terminal_sessions_info', handleSessionsInfo);
        socket.off('terminal_port_mappings', handlePortMappings);
      };
    }, [
      session,
      onConnect,
      onDisconnect,
      isReadOnly,
      sessionId,
      sendAiTerminalData,
      clearRetryTimeout,
      scheduleRetry
    ]);

    useImperativeHandle(
      ref,
      () => ({
        clearTerminal,
        clearExecutedCommands,
        getSessions,
        sendHistoryToChat,
        connectWebSocket,
        forceReconnect,
        disconnectWebSocket,
        getPortMappings: () => portMappings,
        sendCommand: (command: string) => {
          if (accountExpiredRef.current) return;
          if (session?.socket && isConnected) {
            const trimmed = command.trim();
            const isControlOnly =
              trimmed.length === 0 ||
              (trimmed.length === 1 && trimmed.charCodeAt(0) < 32);
            if (!isReadOnlyRef.current && !isControlOnly) {
              beginRecord(trimmed);
              lineBufferRef.current = '';
            }
            session.socket.emit('terminal_io', {
              terminal_id: terminalId.current,
              type: 'command',
              data: command
            });
          } else {
            console.log('[Terminal] Not connected to server');
          }
        }
      }),
      [
        clearTerminal,
        clearExecutedCommands,
        getSessions,
        sendHistoryToChat,
        connectWebSocket,
        forceReconnect,
        disconnectWebSocket,
        session,
        isConnected,
        portMappings,
        beginRecord
      ]
    );

    return (
      <>
        <style>
          {`
          .xterm-container {
            padding: 0;
            height: 100%;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          
          @media (max-width: 768px) {
            .xterm-container {
              padding: 0;
            }
            .xterm-container .xterm-viewport {
              padding-bottom: 0;
            }
          }
          .xterm-container .xterm {
            flex: 1;
            overflow: hidden;
            padding-bottom: 0;
          }
          .xterm-container .xterm-viewport {
            overflow-x: hidden !important;
            overflow-y: auto !important;
            padding-bottom: 0;
          }
          .xterm-container .xterm-screen {
            padding-bottom: 0;
            overflow: hidden;
          }
          .xterm-container .xterm-textarea {
            overflow: hidden;
          }
          .xterm-container .xterm-cursor {
            overflow: hidden;
          }
          .xterm .xterm-rows {
            box-sizing: border-box;
          }
          
          .xterm-container .xterm-viewport::-webkit-scrollbar {
            width: 8px;
          }
          
          .xterm-container .xterm-viewport::-webkit-scrollbar-track {
            background: var(--scrollbar-track-bg);
            border-radius: 4px;
            margin: 4px 0;
          }
          
          .xterm-container .xterm-viewport::-webkit-scrollbar-thumb {
            background: var(--scrollbar-thumb-bg);
            border-radius: 4px;
            border: 1px solid var(--scrollbar-thumb-border);
            transition: all 0.2s ease-in-out;
          }
          
          .xterm-container .xterm-viewport::-webkit-scrollbar-thumb:hover {
            background: var(--scrollbar-thumb-hover-bg);
            transform: scaleX(1.1);
          }
          
          .xterm-container .xterm-viewport::-webkit-scrollbar-thumb:active {
            background: var(--scrollbar-thumb-active-bg);
            transform: scaleX(1.2);
          }
          
          .xterm-container .xterm-viewport::-webkit-scrollbar-corner {
            background: transparent;
          }
          
          .xterm-container .xterm-viewport {
            scrollbar-width: thin;
            scrollbar-color: var(--scrollbar-thumb-bg) var(--scrollbar-track-bg);
          }
        `}
        </style>
        <CardSquare
          className={cn(
            currentStyles.card,
            'flex flex-col h-full w-full max-w-full max-h-full shadow-2xl backdrop-blur-sm',
            className
          )}
        >
          {showHeader && (
            <CardHeader
              className={cn(
                currentStyles.header,
                'flex-none px-2 border-b border-slate-600/50 backdrop-blur-sm min-h-fit py-0 w-full flex items-center justify-between gap-2'
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={cn(
                    currentStyles.icon,
                    'p-1 rounded-md flex items-center justify-center'
                  )}
                >
                  <TerminalIcon
                    className={cn(currentStyles.iconColor, 'h-4 w-4')}
                    aria-label="Terminal"
                  />
                </div>
                {sessionId && (
                  <span
                    className="text-xs text-muted-foreground truncate max-w-[100px] cursor-pointer font-mono"
                    title={sessionId}
                  >
                    Session: {sessionId.slice(0, 8)}...
                  </span>
                )}
                <span className="sr-only">Terminal status</span>
                <span
                  className={cn(
                    'ml-2 w-2 h-2 rounded-full',
                    isConnecting
                      ? 'bg-yellow-400 animate-pulse'
                      : isConnected
                        ? 'bg-green-400'
                        : 'bg-red-400'
                  )}
                  title={
                    isConnecting
                      ? 'Connecting'
                      : isConnected
                        ? 'Connected'
                        : 'Disconnected'
                  }
                />
              </div>
            </CardHeader>
          )}
          <CardContent
            className={cn(
              'flex-1 min-h-0 flex flex-col p-0 w-full max-w-full overflow-hidden',
              currentStyles.content
            )}
          >
            <div
              ref={terminalRef}
              className={cn(
                'flex-1 min-h-0 w-full max-w-full h-full max-h-full shadow-inner overflow-hidden xterm-container relative',
                currentStyles.content
              )}
              style={
                {
                  maxWidth: '100%',
                  maxHeight: '100%',
                  height: '100%',
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  '--scrollbar-track-bg':
                    variant === 'dark'
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(241, 245, 249, 0.5)',
                  '--scrollbar-thumb-bg':
                    variant === 'dark'
                      ? 'rgba(148, 163, 184, 0.6)'
                      : 'rgba(100, 116, 139, 0.6)',
                  '--scrollbar-thumb-border':
                    variant === 'dark'
                      ? 'rgba(51, 65, 85, 0.3)'
                      : 'rgba(203, 213, 225, 0.3)',
                  '--scrollbar-thumb-hover-bg':
                    variant === 'dark'
                      ? 'rgba(148, 163, 184, 0.8)'
                      : 'rgba(100, 116, 139, 0.8)',
                  '--scrollbar-thumb-active-bg':
                    variant === 'dark'
                      ? 'rgba(148, 163, 184, 1)'
                      : 'rgba(100, 116, 139, 1)'
                } as React.CSSProperties
              }
            />
          </CardContent>
        </CardSquare>
      </>
    );
  }
);

export default TerminalComponent;
